import { useMemo, useState } from 'react';
import {
  usePatchIncident, useShelterCheckIn,
  type Facility, type IncidentSite,
} from '../api';
import { siteKindMeta, formatTerm } from '../incidentVocab';

/**
 * Command post, shelters, staging — the places an incident is run from and the
 * places people are sent to.
 *
 * Shelter occupancy is not a separate roster. It counts anyone whose recorded
 * location is this facility, whether they came off the ward list or walked in
 * off the street, because a second list of who is safe is how someone gets
 * counted twice or missed entirely. Checking a family in here also accounts
 * for them on the board — one action.
 */
export default function Facilities({ incidentId, facilities, sites, commandPostId }: {
  incidentId: string;
  facilities: Facility[];
  sites: IncidentSite[];
  commandPostId: string | null;
}) {
  const patchIncident = usePatchIncident();

  // Candidate command posts: anywhere non-residential, plus wherever is
  // already set. Early on the honest answer is "my car", so a car added as an
  // on-the-fly site is a legitimate choice here.
  const candidates = useMemo(
    () => sites.filter((s) => s.site_kind !== 'residence' || s.id === commandPostId),
    [sites, commandPostId],
  );

  if (facilities.length === 0 && !commandPostId) {
    return (
      <section className="rounded border border-dashed border-slate-300 dark:border-slate-700 px-3 py-2 text-xs text-slate-500 dark:text-slate-400 print:hidden">
        No command post or shelter set. Add an address and set its type to
        Command post or Shelter — the meetinghouse, your house, or your car.
      </section>
    );
  }

  return (
    <section className="space-y-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 print:hidden">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Operating from
        </span>
        <select
          value={commandPostId ?? ''}
          onChange={(e) =>
            patchIncident.mutate({
              id: incidentId,
              body: { command_post_site_id: e.target.value || null },
            })
          }
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
        >
          <option value="">— not set —</option>
          {candidates.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </div>

      {facilities.map((f) => (
        <FacilityRow key={f.id} facility={f} incidentId={incidentId}
                     isCommandPost={f.id === commandPostId} />
      ))}
    </section>
  );
}

function FacilityRow({ facility: f, incidentId, isCommandPost }: {
  facility: Facility; incidentId: string; isCommandPost: boolean;
}) {
  const checkIn = useShelterCheckIn();
  const [open, setOpen] = useState(f.site_kind === 'shelter');
  const kind = siteKindMeta(f.site_kind);
  const over = f.capacity != null && f.occupancy > f.capacity;
  const near = f.capacity != null && !over && f.occupancy >= f.capacity * 0.85;

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-2 py-1 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
      >
        <span className="text-sm text-slate-800 dark:text-slate-200">
          <span className="mr-1 text-slate-400">{kind.glyph}</span>
          {f.label}
          <span className="ml-1.5 text-xs text-slate-500 dark:text-slate-400">{kind.label}</span>
          {isCommandPost && (
            <span className="ml-1.5 rounded bg-slate-800 px-1 text-xs text-white dark:bg-slate-200 dark:text-slate-900">
              command
            </span>
          )}
        </span>
        <span className="flex items-center gap-2 text-xs tabular-nums">
          {/* Knowing you are at 90% before the next family arrives is the
              difference between redirecting them and turning them away. */}
          <span className={
            over ? 'font-semibold text-rose-700 dark:text-rose-300'
                 : near ? 'text-amber-700 dark:text-amber-300'
                        : 'text-slate-600 dark:text-slate-300'}>
            {f.occupancy}{f.capacity != null && ` / ${f.capacity}`}
            {over && ' over capacity'}
          </span>
        </span>
      </button>

      {open && (
        <div className="border-t border-slate-200 dark:border-slate-700 px-2 py-1.5 text-xs">
          {f.occupancy === 0 ? (
            <p className="italic text-slate-400 dark:text-slate-500">
              Nobody here yet. Mark people “found at” this location from their status popover,
              or add walk-ins as people at this address.
            </p>
          ) : (
            <>
              <p className="mb-1 text-slate-500 dark:text-slate-400">
                {f.adults} adults · {f.children} children · {f.seniors} seniors
                {f.unknown_age > 0 && ` · ${f.unknown_age} age unknown`}
                {f.with_medical > 0 && (
                  <span className="text-violet-700 dark:text-violet-300"> · {f.with_medical} with medical needs</span>
                )}
              </p>
              <ul className="space-y-0.5">
                {f.people.map((p) => (
                  <li key={p.id} className="flex items-baseline gap-1.5">
                    <span className="text-slate-700 dark:text-slate-200">{p.name}</span>
                    {p.age != null && <span className="text-slate-400">{p.age}</span>}
                    {(p.medical_needs || p.special_needs.length > 0) && (
                      <span className="text-violet-600 dark:text-violet-400"
                            title={[...p.special_needs.map(formatTerm), p.medical_needs].filter(Boolean).join(' · ')}>
                        ✚
                      </span>
                    )}
                    <button
                      onClick={() => checkIn.mutate({ incidentId, id: f.id, person_ids: [p.id], departed: true })}
                      disabled={checkIn.isPending}
                      className="ml-auto text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                      title="Record that they have left"
                    >
                      depart
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {checkIn.error && (
            <p className="mt-1 text-rose-600 dark:text-rose-400">{(checkIn.error as Error).message}</p>
          )}
        </div>
      )}
    </div>
  );
}
